/**
 * 코트 요금 계약 사본.
 *
 * - 요일유형 축(`GET/PUT …/courts/{courtId}/price-bands`): **`#852` 허브 게시본 §2-1·§2-2** 그대로.
 * - 날짜 축(`GET/PUT/DELETE …/price-dates[/{date}]`)·잔여 시간대(`…/price-bands/remaining-windows`):
 *   **`#866` 설계 §2-2 API ④⑤ + §6**(구현 완료본) 기준. 🚨 이 두 축은 허브 `outputs/api-spec.md` 가
 *   아직 게시되지 않아 **응답 필드명이 가정**이다(설계 §위험 R1) — 게시본이 올라오면 이 파일 하나만 대조하면
 *   되도록 사본을 여기 모아 둔다. 그래서 소비부는 방어적으로 읽는다(`priceApi.ts` 의 정규화 참고).
 *
 * `courtTypes.ts`·`operatingHourTypes.ts` 와 같은 규칙 — 서버가 계약을 바꾸지 않는 한 필드를 파생·개명하지
 * 않는다.
 */

import type { VenueCourtStatus } from './courtTypes'

/** 🚫 공휴일은 서버가 판정하지 않는다 — 공휴일 요금은 **날짜 축**으로 지정한다(`#852` §1, `#866` D5). */
export type CourtPriceDayType = 'WEEKDAY' | 'WEEKEND'

/**
 * 요일유형·날짜 두 축이 공유하는 구간 표현.
 * 🚨 **반열림 구간**이다 — 시작 포함, 종료 제외. `12:00~18:00` 과 `18:00~22:00` 은 겹치지 않는다(`#852` §1).
 */
export interface CourtPriceBand {
  /** "HH:mm". */
  startTime: string
  /** "HH:mm". 자정 마감은 "24:00"(`23:59` 이 아니다). */
  endTime: string
  /** 서버가 만든 문구 — 화면이 조립하지 않는다. */
  timeRangeLabel: string
  hourlyFee: number
  /** *"이 구간이 칸 하나를 통째로 덮을 때"* 의 금액(안내용). 🚫 결제 금액으로 쓰지 않는다(`#852` §2-1). */
  slotFee?: number
}

export interface CourtPriceDayTypeGroup {
  dayType: CourtPriceDayType
  dayTypeLabel: string
  /** 구간이 덮지 않는 시간대에 적용되는 기본가. */
  baseHourlyFee: number
  minSlotFee: number
  maxSlotFee: number
  /** 칸 금액이 두 종류 이상인가. 🚫 `minSlotFee != maxSlotFee` 로 직접 판정하지 않는다. */
  feeVaries: boolean
  bands: CourtPriceBand[]
  /** 구간이 운영시간 밖이라 효과가 없을 때만 채워진다(`#852` §2-2 — 허용하되 안내). */
  notice: string | null
}

export interface CourtPriceBandView {
  baseTime: string
  venueId: string
  courtId: number
  courtName: string
  status: VenueCourtStatus
  statusLabel: string
  /** 지금 저장할 수 있는가 = `PUT` 이 통과하는 조건과 같은 판정. */
  editable: boolean
  editBlockReason: string | null
  editBlockNotice: string | null
  /** 🚫 이 화면에서 바꿀 수 없다(운영시간 설정 EP 가 소유). */
  reservationUnitMinutes: number
  reservationUnitLabel: string
  /** 🔒 상한도 서버가 준다 — 프런트 하드코딩 금지(`#852` §2-1). */
  maxPriceBandsPerDayType: number
  hasPriceBands: boolean
  /** 🚨 항상 2행, `WEEKDAY` → `WEEKEND` 순서 고정. 구간이 없어도 행이 사라지지 않는다. */
  dayTypes: CourtPriceDayTypeGroup[]
  updatedAt: string | null
}

/** `PUT …/price-bands` 요청 바디(`#852` §2-2). 🚨 **전체 치환** — `bands: []` 면 구간이 모두 삭제된다. */
export interface CourtPriceBandSaveInput {
  weekdayHourlyFee: number
  weekendHourlyFee: number
  bands: {
    dayType: CourtPriceDayType
    startTime: string
    endTime: string
    hourlyFee: number
  }[]
}

/** 날짜 1건(`#866` API ④). */
export interface CourtPriceDateItem {
  /** "yyyy-MM-dd" (KST 달력 날짜). */
  date: string
  /** 예: "설날". 🚫 **표기 전용** — 판정에 쓰지 않는다(`#866` R11). */
  label: string | null
  bands: CourtPriceBand[]
}

export interface CourtPriceDateListView {
  baseTime: string
  courtId: number
  from: string
  to: string
  dates: CourtPriceDateItem[]
}

/**
 * `PUT …/price-dates/{date}` 요청 바디(`#866` API ④).
 * 🚨 **그 날짜의 구간 전량 교체**다. `bands: []` = 그날 규칙 삭제(요일유형 규칙으로 복귀) = `DELETE` 와 동치.
 */
export interface CourtPriceDateSaveInput {
  label?: string | null
  bands: { startTime: string; endTime: string; hourlyFee: number }[]
}

/** 지금 그 시간대에 무슨 값이 먹고 있는가 — 날짜 > 요일유형 > 기본가 3단 우선순위(`#866` API ⑤). */
export type CourtPriceAppliedFrom = 'DATE' | 'DAY_TYPE' | 'BASE_FEE'

export interface CourtRemainingWindow {
  startTime: string
  endTime: string
  durationMinutes: number
  appliedHourlyFee: number
  appliedFrom: CourtPriceAppliedFrom
}

export interface CourtRemainingWindowsView {
  axis: 'DAY_TYPE' | 'DATE'
  dayType?: CourtPriceDayType
  dayTypeLabel?: string
  date?: string
  /** 🚨 하루 전체(00:00~24:00) 기준이며 **운영시간과 교집합하지 않는다**(`#866` D8). */
  windows: CourtRemainingWindow[]
  fullyCovered: boolean
}

/** 날짜 목록 조회 기본 범위(`#866` API ④ — 생략 시 서버도 오늘~+90일). 화면이 명시적으로 보낸다. */
export const PRICE_DATE_RANGE_DAYS = 90

/** `#866` API ④ — 서버가 거부하는 상한(요일유형 축의 `maxPriceBandsPerDayType` 를 재사용한다). */
export const PRICE_DATE_MAX_BANDS_FALLBACK = 24
